// @ts-check

/**
 * Accordion disclosure Web Component.
 * Extends <details> with animated height transitions.
 *
 * @example
 * <details is="accordion-disclosure">
 *   <summary>Title</summary>
 *   <div class="accordion__content">Content</div>
 * </details>
 */
(() => {
  class AccordionDisclosure extends HTMLDetailsElement {
    constructor() {
      super();

      /** @type {HTMLElement} */
      this.summaryEl = /** @type {HTMLElement} */ (this.firstElementChild);
      /** @type {HTMLElement} */
      this.contentEl = /** @type {HTMLElement} */ (this.lastElementChild);

      this.summaryEl.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });

      // Shopify theme editor integration
      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', () => {
          this.open = true;
        });
        this.addEventListener('shopify:block:deselect', () => {
          this.open = false;
        });
      }
    }

    toggle() {
      if (this.open) {
        this.close();
      } else {
        this.expand();
      }
    }

    async expand() {
      const startHeight = `${this.summaryEl.offsetHeight}px`;
      const endHeight = `${this.scrollHeight}px`;

      this.setAttribute('open', '');
      this.setAttribute('aria-expanded', 'true');
      this.style.overflow = 'hidden';

      const duration = parseFloat(
        getComputedStyle(this).getPropertyValue('--duration-normal') || '300'
      );
      const easing =
        getComputedStyle(this).getPropertyValue('--ease-standard').trim() ||
        'ease';

      const anim = this.animate(
        { height: [startHeight, endHeight] },
        { duration, easing }
      );

      this.contentEl.animate(
        { opacity: [0, 1], transform: ['translateY(-4px)', 'translateY(0)'] },
        { duration: duration * 0.6, easing }
      );

      await anim.finished;
      this.style.overflow = '';
      this.style.height = '';
    }

    async close() {
      const startHeight = `${this.offsetHeight}px`;
      const endHeight = `${this.summaryEl.offsetHeight}px`;

      this.setAttribute('aria-expanded', 'false');
      this.style.overflow = 'hidden';

      const duration = parseFloat(
        getComputedStyle(this).getPropertyValue('--duration-fast') || '150'
      );
      const easing =
        getComputedStyle(this).getPropertyValue('--ease-accelerate').trim() ||
        'ease';

      this.contentEl.animate({ opacity: [1, 0] }, { duration: duration * 0.6 });

      const anim = this.animate(
        { height: [startHeight, endHeight] },
        { duration, easing }
      );

      await anim.finished;
      this.removeAttribute('open');
      this.style.overflow = '';
      this.style.height = '';
    }
  }

  customElements.define('accordion-disclosure', AccordionDisclosure, {
    extends: 'details',
  });
})();
